import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  switchMap,
  map,
  catchError,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ToastService } from '@shared/components/toast/services/toast.service';
import { UserListService } from '../services/users.service';
import { UserListActions } from './users.actions';
import * as UserListSelectors from './users.selectors';

/**
 * Backend error code returned when the user already has a pending approval
 * request that conflicts with the requested change. The user-list screen
 * surfaces a dedicated modal for this case instead of a generic toast.
 */
const DUPLICATE_PENDING_APPROVAL = 'DUPLICATE_PENDING_APPROVAL';

@Injectable()
export class UserListEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(UserListService);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);

  /**
   * Load user list effect
   */
  readonly loadUserList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.loadUserList),
      withLatestFrom(
        this.store.select(UserListSelectors.selectUserListPagination)
      ),
      switchMap(([{ params }, pagination]) => {
        // Use params from action if provided, otherwise use current pagination from store
        const queryParams = params || {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
        };
        // Always exclude self from user list
        const finalParams = { ...queryParams, excludeSelf: true };
        return this.service.getUserList(finalParams).pipe(
          map(response => UserListActions.loadUserListSuccess({ response })),
          catchError(err =>
            of(
              UserListActions.loadUserListFailure({
                error: this.normalizeError(err),
              })
            )
          )
        );
      })
    )
  );

  /**
   * Handle load failure
   */
  readonly loadUserListFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.loadUserListFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  /**
   * Freeze user effect
   */
  readonly freezeUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.freezeUser),
      switchMap(({ userId }) =>
        this.service.freezeUser(userId).pipe(
          map(() => UserListActions.freezeUserSuccess({ userId })),
          catchError(err =>
            of(
              UserListActions.freezeUserFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Freeze user success - reload user list
   */
  readonly freezeUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.freezeUserSuccess),
      map(() => UserListActions.loadUserList({}))
    )
  );

  /**
   * Handle freeze failure
   */
  readonly freezeUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.freezeUserFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  /**
   * Unfreeze user effect
   */
  readonly unfreezeUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.unfreezeUser),
      switchMap(({ userId }) =>
        this.service.unfreezeUser(userId).pipe(
          map(() => UserListActions.unfreezeUserSuccess({ userId })),
          catchError(err =>
            of(
              UserListActions.unfreezeUserFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Unfreeze user success - reload user list
   */
  readonly unfreezeUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.unfreezeUserSuccess),
      map(() => UserListActions.loadUserList({}))
    )
  );

  /**
   * Handle unfreeze failure
   */
  readonly unfreezeUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.unfreezeUserFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  /**
   * Block user effect
   */
  readonly blockUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.blockUser),
      switchMap(({ userId }) =>
        this.service.blockUser(userId).pipe(
          map(() => UserListActions.blockUserSuccess({ userId })),
          catchError(err =>
            of(
              UserListActions.blockUserFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Block user success - reload user list
   */
  readonly blockUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.blockUserSuccess),
      map(() => UserListActions.loadUserList({}))
    )
  );

  /**
   * Handle block failure
   */
  readonly blockUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.blockUserFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  /**
   * Unblock user effect
   */
  readonly unblockUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.unblockUser),
      switchMap(({ userId }) =>
        this.service.unblockUser(userId).pipe(
          map(() => UserListActions.unblockUserSuccess({ userId })),
          catchError(err =>
            of(
              UserListActions.unblockUserFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Unblock user success - reload user list
   */
  readonly unblockUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.unblockUserSuccess),
      map(() => UserListActions.loadUserList({}))
    )
  );

  /**
   * Handle unblock failure
   */
  readonly unblockUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.unblockUserFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  /**
   * Update user role effect
   */
  readonly updateUserRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.updateUserRole),
      switchMap(({ userId, role }) =>
        this.service.updateUserRole(userId, role).pipe(
          map(() => UserListActions.updateUserRoleSuccess({ userId, role })),
          catchError(err => {
            const errorCode = this.extractErrorCode(err);
            const message = this.normalizeError(err);
            // `updateUserRole` opts out of the global error toast so we
            // can show a dedicated modal for `DUPLICATE_PENDING_APPROVAL`.
            // Every other error still needs the generic toast.
            if (errorCode !== DUPLICATE_PENDING_APPROVAL) {
              this.toastService.error(message);
            }
            return of(
              UserListActions.updateUserRoleFailure({
                error: message,
                errorCode,
              })
            );
          })
        )
      )
    )
  );

  /**
   * Update user role success - reload user list
   */
  readonly updateUserRoleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.updateUserRoleSuccess),
      map(() => UserListActions.loadUserList({}))
    )
  );

  /**
   * Handle update role failure
   */
  readonly updateUserRoleFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.updateUserRoleFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  /**
   * Add user to wishlist effect
   */
  readonly addUserToWishlist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.addUserToWishlist),
      switchMap(({ email, phone, role }) =>
        this.service.addToWishlist({ email, phone, role }).pipe(
          map(() => UserListActions.addUserToWishlistSuccess()),
          catchError(err =>
            of(
              UserListActions.addUserToWishlistFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Add user to wishlist success - reload user list
   */
  readonly addUserToWishlistSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.addUserToWishlistSuccess),
        tap(() => {
          // Intentionally do not reload here.
          // The user list screen handles refreshing after showing success modal.
        })
      ),
    { dispatch: false }
  );

  /**
   * Handle add user to wishlist failure
   */
  readonly addUserToWishlistFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.addUserToWishlistFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  /**
   * Update wishlist user effect
   */
  readonly updateWishlistUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.updateWishlistUser),
      switchMap(({ userId, email, phone, role }) =>
        this.service.updateWishlistUser(userId, { email, phone, role }).pipe(
          map(() => UserListActions.updateWishlistUserSuccess()),
          catchError(err =>
            of(
              UserListActions.updateWishlistUserFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Update wishlist user success - reload user list
   */
  readonly updateWishlistUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.updateWishlistUserSuccess),
        tap(() => {
          // Intentionally do not reload here.
          // The user list screen handles refreshing after showing success modal.
        })
      ),
    { dispatch: false }
  );

  /**
   * Handle update wishlist user failure
   */
  readonly updateWishlistUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.updateWishlistUserFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  /**
   * Delete wishlist user effect
   */
  readonly deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListActions.deleteUser),
      switchMap(({ userId }) =>
        this.service.deleteUser(userId).pipe(
          map(() => UserListActions.deleteUserSuccess()),
          catchError(err => {
            const errorCode = this.extractErrorCode(err);
            const message = this.normalizeError(err);
            // `deleteUser` opts out of the global error toast so we can
            // show a dedicated modal for `DUPLICATE_PENDING_APPROVAL`.
            // Every other error still needs the generic toast.
            if (errorCode !== DUPLICATE_PENDING_APPROVAL) {
              this.toastService.error(message);
            }
            return of(
              UserListActions.deleteUserFailure({
                error: message,
                errorCode,
              })
            );
          })
        )
      )
    )
  );

  /**
   * Delete wishlist user success - reload user list
   */
  readonly deleteUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.deleteUserSuccess),
        tap(() => {
          // Intentionally do not reload here.
          // The user list screen triggers refresh on demand.
        })
      ),
    { dispatch: false }
  );

  /**
   * Handle delete wishlist user failure
   */
  readonly deleteUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListActions.deleteUserFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  private extractErrorCode(err: unknown): string | undefined {
    const code = (err as { error?: { errorCode?: unknown } })?.error?.errorCode;
    return typeof code === 'string' ? code : undefined;
  }

  private normalizeError(err: unknown): string {
    const e = err as {
      error?: { message?: string | { message?: string } };
      message?: string;
    };
    if (
      e?.error?.message &&
      typeof e.error.message === 'object' &&
      e.error.message.message
    ) {
      return e.error.message.message;
    }

    if (typeof e?.error?.message === 'string') {
      return e.error.message;
    }

    return e?.message || this.translate.instant('auth.login.errors.generic');
  }
}
